/**
 * O cartão "Orçamento do CRM", dentro da ficha, logo depois dos Produtos (13.22a2, FR-92).
 *
 * **Ele tira a foto do painel de Produtos.** O painel relê o preço a cada abertura (FR-46) e não
 * guarda o que foi oferecido; "Gerar orçamento" grava, no serviço, a foto do instante — número,
 * itens com preço, quantidade e disponibilidade, total à vista, as condições e, com taxa, o total
 * no cartão. A foto não muda depois, nem quando o ERP muda o preço.
 *
 * **As condições são três** (decisão do Gustavo, 26/10/2026): a forma de pagamento (a lista da
 * Configuração), o prazo de produção (texto) e o frete, sempre FOB — só para leitura. A validade
 * não foi marcada.
 *
 * **"Gerar orçamento" fica travado, com a razão ao lado,** no Negócio sem Empresa, sem Produto e com
 * o Carga Máquina fora do ar (o painel de Produtos já disse, com a frase do serviço). A trava é
 * conveniência: quem recusa é o serviço, e a recusa dele aparece **verbatim**.
 *
 * **O cartão não existe para a SDR** (quem o monta é a ficha, pela ausência de `valor`): ela não vê
 * dinheiro (FR-62), e o serviço a recusa com `403` nas três rotas. Esconder é conveniência.
 *
 * **A lista das fotos** mostra número, data em `America/Sao_Paulo`, situação, total à vista e total
 * no cartão.
 *
 * **O desconto (13.22b1, FR-93)** é um percentual sobre o total, digitado em "Desconto (%)". A frase
 * logo abaixo diz o teto da Configuração ("Até X% o orçamento sai pronto; acima, espera
 * aprovação."), lido de `GET /teto-de-desconto`. **Quem decide a situação é o serviço** (o gatilho
 * do banco, pelo teto do instante): a tela só mostra o que a foto gravou — "Pronta" ou "Aguardando
 * aprovação" — e mudar o teto depois não muda foto nenhuma.
 *
 * **A decisão (13.22b2)**: a foto que espera aprovação ganha "Aprovar" e "Recusar" para quem tem
 * "Aprovar desconto" e **não é o próprio** — nem quem gerou a foto, nem a Responsável do Negócio,
 * agora ou no instante da foto (decisão do Gustavo, 26/10/2026). É conveniência: quem decide é o
 * serviço, e a recusa dele aparece verbatim. A lista diz "Aprovada" e "Recusada", e marca
 * "Vigente" na foto cujo total à vista é hoje o valor do Negócio (no máximo uma).
 */

import { type FormEvent, useId, useState } from 'react'

import { ErroDaApi, MENSAGEM_DE_ERRO_DESCONHECIDO } from '@/api/erros'
import { useSessao } from '@/modulos/identidade/sessao'
import {
  type ResumoDoOrcamentoDoCrm,
  type SituacaoDoOrcamentoDoCrm,
  useDecidirDesconto,
  useFormasDePagamento,
  useGerarOrcamentoDoCrm,
  useOrcamentosDoCrm,
  useProdutos,
  useTetoDeDesconto,
} from '@/modulos/negocios/api'
import { DATA_E_HORA, textoDoValor } from '@/modulos/negocios/apresentacao'
import { LinhaDoRotulo } from '@/shell/MarcaDeObrigatorio'
import './orcamento-do-crm.css'

const ID_DO_TITULO = 'orcamento-do-crm-titulo'
const TITULO = 'Orçamento do CRM'
const SELO = 'Foto do instante'

/** O frete é um só, e fixo: o mesmo texto que o serviço grava na foto. */
export const FRETE_FOB = 'FOB, por conta do cliente'

/** O teto do serviço (`TAMANHO_DO_PRAZO_DE_PRODUCAO`). */
export const TAMANHO_DO_PRAZO = 200

export const ROTULO_DA_FORMA = 'Forma de pagamento'
export const ROTULO_DO_PRAZO = 'Prazo de produção'
export const ROTULO_DO_FRETE = 'Frete'
export const ROTULO_DO_DESCONTO = 'Desconto (%)'
export const GERAR = 'Gerar orçamento'

export const SEM_EMPRESA = 'Defina a Empresa para gerar o orçamento.'
export const SEM_PRODUTO = 'Acrescente um produto para gerar o orçamento.'
export const ERP_FORA =
  'O orçamento usa o preço do Carga Máquina neste instante: gere quando ele voltar.'
export const NEGOCIO_FECHADO = 'Negócio ganho ou perdido não gera orçamento.'
export const SEM_FORMA =
  'Nenhuma forma de pagamento ativa. Cadastre uma em Configuração → Negócios.'
export const SEM_FOTO = 'Nenhum orçamento gerado neste negócio.'
export const DESCONTO_INVALIDO =
  'Informe o desconto em percentual, de 0 a menos de 100, com até duas casas.'
export const APROVAR = 'Aprovar'
export const RECUSAR = 'Recusar'
export const VIGENTE = 'Vigente'
export const DESCRICAO_DA_VIGENTE = 'O total à vista deste orçamento é o valor do negócio.'
const CARREGANDO = 'Carregando os orçamentos…'

/** Os rótulos de tela das cinco situações (o enum inteiro nasce na 13.22a2). */
export const ROTULO_DA_SITUACAO: Record<SituacaoDoOrcamentoDoCrm, string> = {
  pronta: 'Pronta',
  aguardando_aprovacao: 'Aguardando aprovação',
  aprovada: 'Aprovada',
  recusada: 'Recusada',
  enviada: 'Enviada',
}

const PERCENTUAL = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

/** `"6.00"` → `"6,00%"`. A taxa é percentual (texto do serviço), e não dinheiro. */
export function textoDaTaxa(taxa: string): string {
  return `${PERCENTUAL.format(Number(taxa))}%`
}

/** A frase do teto, com o percentual que a Configuração gravou (`"5.00"` → "Até 5,00% …"). */
export function fraseDoTeto(percentual: string): string {
  return `Até ${textoDaTaxa(percentual)} o orçamento sai pronto; acima, espera aprovação.`
}

/**
 * O desconto digitado, no formato do contrato (`"8.5"`, com ponto); vazio é `"0"`. Devolve
 * `undefined` quando o texto não é um percentual de 0 a menos de 100 com até duas casas — a recusa
 * local que evita a ida à rede com um `422` genérico (o serviço continua sendo quem decide).
 */
export function descontoDoCampo(texto: string): string | undefined {
  const limpo = texto.trim().replace(',', '.')
  if (limpo === '') return '0'
  if (!/^\d{1,2}(\.\d{1,2})?$/.test(limpo)) return undefined
  return limpo
}

/** O aviso de "gerado": o número e se a foto nasceu esperando aprovação (o serviço decidiu). */
interface FotoGerada {
  numero: number
  aguardando: boolean
}

function mensagemDe(falha: unknown): string {
  return falha instanceof ErroDaApi ? falha.message : MENSAGEM_DE_ERRO_DESCONHECIDO
}

/** O aviso de "decidido": o número e o que foi decidido (o serviço gravou). */
interface FotoDecidida {
  numero: number
  aprovado: boolean
}

/** A decisão de uma linha: os dois botões, travados enquanto alguma decisão está no ar. */
interface DecisaoDaLinha {
  pendente: boolean
  aoDecidir: (aprovado: boolean) => void
}

function LinhaDaFoto({
  foto,
  decisao,
  falha,
}: {
  foto: ResumoDoOrcamentoDoCrm
  /** Ausente quando quem lê não decide esta foto (conveniência: quem decide é o serviço). */
  decisao?: DecisaoDaLinha
  /** A recusa do serviço a esta decisão, verbatim. */
  falha?: string
}) {
  const aVista = textoDoValor(foto.valor_a_vista).texto
  const noCartao = foto.valor_no_cartao === null ? null : textoDoValor(foto.valor_no_cartao).texto
  return (
    <li className="orcamento-crm__foto" data-vigente={foto.vigente}>
      <div className="orcamento-crm__foto-topo">
        <span className="orcamento-crm__numero">{`Nº ${String(foto.numero)}`}</span>
        <span className="orcamento-crm__selos">
          {foto.vigente ? (
            <span className="orcamento-crm__vigente" title={DESCRICAO_DA_VIGENTE}>
              {VIGENTE}
              <span className="ficha__so-para-leitor">{`: ${DESCRICAO_DA_VIGENTE}`}</span>
            </span>
          ) : null}
          <span className="orcamento-crm__situacao" data-situacao={foto.situacao}>
            {ROTULO_DA_SITUACAO[foto.situacao]}
          </span>
        </span>
      </div>
      <span className="orcamento-crm__data">
        {`${DATA_E_HORA.format(new Date(foto.gerado_em))} · ${foto.forma_de_pagamento_rotulo}`}
        {Number(foto.desconto_percentual) === 0
          ? null
          : ` · desconto de ${textoDaTaxa(foto.desconto_percentual)}`}
      </span>
      <div className="orcamento-crm__totais">
        <span className="orcamento-crm__par">
          <span className="orcamento-crm__rotulo">À vista</span>
          <span className="orcamento-crm__valor">{aVista}</span>
        </span>
        {noCartao === null ? null : (
          <span className="orcamento-crm__par">
            <span className="orcamento-crm__rotulo">No cartão</span>
            <span className="orcamento-crm__valor">{noCartao}</span>
          </span>
        )}
      </div>
      {decisao === undefined ? null : (
        <div className="orcamento-crm__decisao">
          <button
            type="button"
            className="ficha__botao ficha__botao--primario"
            disabled={decisao.pendente}
            aria-label={`${APROVAR} o desconto do orçamento nº ${String(foto.numero)}`}
            onClick={() => {
              decisao.aoDecidir(true)
            }}
          >
            {APROVAR}
          </button>
          <button
            type="button"
            className="ficha__botao"
            disabled={decisao.pendente}
            aria-label={`${RECUSAR} o desconto do orçamento nº ${String(foto.numero)}`}
            onClick={() => {
              decisao.aoDecidir(false)
            }}
          >
            {RECUSAR}
          </button>
        </div>
      )}
      {falha === undefined ? null : (
        /* Verbatim: o `403` de quem não decide, o `409` da já decidida e o do Negócio fechado. */
        <p className="ficha__erro" role="alert">
          {falha}
        </p>
      )}
    </li>
  )
}

export function OrcamentoDoCrm({
  negocioId,
  responsavelId,
  fechado,
  semEmpresa,
  somenteLeitura = false,
  excluido = false,
}: {
  negocioId: string
  /** A Responsável **atual** do Negócio: ela não decide o desconto dele (13.22b2). */
  responsavelId: string
  fechado: boolean
  semEmpresa: boolean
  /** A leitura pela tarefa (13.14c): mostra as fotos e não gera. */
  somenteLeitura?: boolean
  /** O Negócio está saindo pela exclusão: nada é pedido de novo. */
  excluido?: boolean
}) {
  const fotos = useOrcamentosDoCrm(negocioId, { habilitada: !excluido })
  const podeGerar = !fechado && !somenteLeitura && !excluido
  const formas = useFormasDePagamento({ habilitada: podeGerar })
  /* A mesma chave do cartão de Produtos: uma leitura só, e é dela que vem "sem Produto" e o ERP. */
  const produtos = useProdutos(negocioId, { habilitada: podeGerar })
  /* O teto só informa a frase: a decisão é do serviço, e sem ele (erro) a frase some. */
  const teto = useTetoDeDesconto({ habilitada: podeGerar })
  const gerar = useGerarOrcamentoDoCrm(negocioId)
  const decidir = useDecidirDesconto(negocioId)
  const { data: sessao } = useSessao()
  /* Conveniência (13.22b2): quem tem a permissão, com o Negócio aberto e escrito por esta tela. */
  const decideAlguma =
    sessao !== undefined &&
    'aprovar_desconto' in sessao.permissoes &&
    !fechado &&
    !somenteLeitura &&
    !excluido
  const [decidida, setDecidida] = useState<FotoDecidida | null>(null)
  const [fotoDaFalha, setFotoDaFalha] = useState<string | null>(null)

  function decideEsta(foto: ResumoDoOrcamentoDoCrm): boolean {
    if (!decideAlguma || foto.situacao !== 'aguardando_aprovacao') return false
    /* "Nunca o próprio": quem gerou, a Responsável de agora e a do instante da foto. */
    const proprios = [foto.gerado_por, foto.responsavel_id_no_instante, responsavelId]
    return !proprios.includes(sessao.usuario_id)
  }

  function aoDecidir(foto: ResumoDoOrcamentoDoCrm, aprovado: boolean) {
    setDecidida(null)
    setFotoDaFalha(foto.id)
    decidir.mutate(
      { orcamentoId: foto.id, aprovado },
      {
        onSuccess: () => {
          setDecidida({ numero: foto.numero, aprovado })
        },
      },
    )
  }

  const [formaEscolhida, setFormaEscolhida] = useState('')
  const [prazo, setPrazo] = useState('')
  const [desconto, setDesconto] = useState('')
  const [gerada, setGerada] = useState<FotoGerada | null>(null)

  const idDaForma = useId()
  const idDoPrazo = useId()
  const idDoFrete = useId()
  const idDoDesconto = useId()
  const idDaFraseDoTeto = useId()
  const idDaRazao = useId()
  const idDaDescricao = useId()

  const ativas = formas.data ?? []
  const formaId = formaEscolhida !== '' ? formaEscolhida : (ativas[0]?.id ?? '')
  const forma = ativas.find((item) => item.id === formaId)
  const erpFora =
    produtos.isError && produtos.error instanceof ErroDaApi && produtos.error.status === 503
  const semProduto = produtos.data !== undefined && produtos.data.itens.length === 0

  const descontoPedido = descontoDoCampo(desconto)
  const razao = semEmpresa
    ? SEM_EMPRESA
    : erpFora
      ? ERP_FORA
      : semProduto
        ? SEM_PRODUTO
        : formas.data !== undefined && ativas.length === 0
          ? SEM_FORMA
          : descontoPedido === undefined
            ? DESCONTO_INVALIDO
            : null
  const travado =
    razao !== null ||
    produtos.data === undefined ||
    forma === undefined ||
    prazo.trim() === '' ||
    descontoPedido === undefined

  function aoGerar(evento: FormEvent<HTMLFormElement>) {
    evento.preventDefault()
    if (travado || forma === undefined || descontoPedido === undefined) return
    setGerada(null)
    gerar.mutate(
      {
        forma_de_pagamento_id: forma.id,
        prazo_de_producao: prazo.trim(),
        desconto_percentual: descontoPedido,
      },
      {
        onSuccess: (foto) => {
          setGerada({
            numero: foto.numero,
            aguardando: foto.situacao === 'aguardando_aprovacao',
          })
        },
      },
    )
  }

  const lista = fotos.isError ? (
    /* Verbatim: o serviço é quem diz por que não leu. */
    <p className="ficha__erro" role="alert">
      {mensagemDe(fotos.error)}
    </p>
  ) : fotos.isPending ? (
    <p className="ficha__ajuda" role="status">
      {CARREGANDO}
    </p>
  ) : fotos.data.length === 0 ? (
    <p className="ficha__vazio">{SEM_FOTO}</p>
  ) : (
    <ul className="orcamento-crm__fotos" aria-label="Orçamentos gerados">
      {fotos.data.map((foto) => (
        <LinhaDaFoto
          key={foto.id}
          foto={foto}
          decisao={
            decideEsta(foto)
              ? {
                  pendente: decidir.isPending,
                  aoDecidir: (aprovado) => {
                    aoDecidir(foto, aprovado)
                  },
                }
              : undefined
          }
          falha={
            decidir.isError && fotoDaFalha === foto.id ? mensagemDe(decidir.error) : undefined
          }
        />
      ))}
    </ul>
  )

  return (
    <section className="ficha__card" aria-labelledby={ID_DO_TITULO}>
      <div className="ficha__card-topo">
        <span className="ficha__card-titulo" id={ID_DO_TITULO}>
          {TITULO}
        </span>
        <span className="ficha__selo">{SELO}</span>
      </div>
      <div className="ficha__card-corpo">
        {fechado ? (
          <p className="ficha__ajuda">{NEGOCIO_FECHADO}</p>
        ) : !podeGerar ? null : (
          <form className="orcamento-crm__formulario" onSubmit={aoGerar}>
            <div className="orcamento-crm__campo">
              <label className="ficha__linha-rotulo" htmlFor={idDaForma}>
                {ROTULO_DA_FORMA}
              </label>
              <select
                id={idDaForma}
                className="ficha__entrada"
                value={formaId}
                disabled={ativas.length === 0}
                aria-describedby={forma === undefined ? undefined : idDaDescricao}
                onChange={(evento) => {
                  setFormaEscolhida(evento.target.value)
                }}
              >
                {ativas.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.rotulo}
                  </option>
                ))}
              </select>
              {forma === undefined ? null : (
                /* A descrição e a taxa são duas frases, cada uma na sua linha: a descrição é texto
                   da gestão e pode vir sem ponto final. */
                <div className="orcamento-crm__descricao" id={idDaDescricao}>
                  {forma.descricao ? <p className="ficha__ajuda">{forma.descricao}</p> : null}
                  {forma.taxa_percentual === null ? null : (
                    <p className="ficha__ajuda">
                      {`O total no cartão soma ${textoDaTaxa(forma.taxa_percentual)}.`}
                    </p>
                  )}
                </div>
              )}
            </div>
            <div className="orcamento-crm__campo">
              <LinhaDoRotulo obrigatorio>
                <label className="ficha__linha-rotulo" htmlFor={idDoPrazo}>
                  {ROTULO_DO_PRAZO}
                </label>
              </LinhaDoRotulo>
              <input
                id={idDoPrazo}
                className="ficha__entrada"
                type="text"
                required
                aria-required="true"
                maxLength={TAMANHO_DO_PRAZO}
                placeholder="Ex.: 20 dias úteis"
                value={prazo}
                onChange={(evento) => {
                  setPrazo(evento.target.value)
                }}
              />
            </div>
            <div className="orcamento-crm__campo">
              <label className="ficha__linha-rotulo" htmlFor={idDoDesconto}>
                {ROTULO_DO_DESCONTO}
              </label>
              <input
                id={idDoDesconto}
                className="ficha__entrada"
                type="text"
                inputMode="decimal"
                placeholder="0"
                value={desconto}
                aria-invalid={descontoPedido === undefined ? true : undefined}
                aria-describedby={teto.data === undefined ? undefined : idDaFraseDoTeto}
                onChange={(evento) => {
                  setDesconto(evento.target.value)
                }}
              />
              {teto.data === undefined ? null : (
                <p className="ficha__ajuda" id={idDaFraseDoTeto}>
                  {fraseDoTeto(teto.data.percentual)}
                </p>
              )}
            </div>
            <div className="orcamento-crm__campo">
              <label className="ficha__linha-rotulo" htmlFor={idDoFrete}>
                {ROTULO_DO_FRETE}
              </label>
              <input
                id={idDoFrete}
                className="ficha__entrada"
                type="text"
                readOnly
                value={FRETE_FOB}
              />
            </div>
            <div className="orcamento-crm__acoes">
              <button
                type="submit"
                className="ficha__botao ficha__botao--primario"
                disabled={travado || gerar.isPending}
                aria-describedby={razao === null ? undefined : idDaRazao}
              >
                {GERAR}
              </button>
            </div>
            {razao === null ? null : (
              <p className="ficha__ajuda" id={idDaRazao}>
                {razao}
              </p>
            )}
            {gerar.isError ? (
              /* Verbatim: `orcamento_sem_produtos`, `produto_sem_preco`, o `409` e o `503`. */
              <p className="ficha__erro" role="alert">
                {mensagemDe(gerar.error)}
              </p>
            ) : null}
            {gerada === null || gerar.isError ? null : (
              <p className="orcamento-crm__sucesso" role="status">
                {gerada.aguardando
                  ? `Orçamento nº ${String(gerada.numero)} gerado. O desconto passa do teto: ele espera aprovação.`
                  : `Orçamento nº ${String(gerada.numero)} gerado.`}
              </p>
            )}
          </form>
        )}
        {lista}
        {decidida === null || decidir.isError ? null : (
          <p className="orcamento-crm__sucesso" role="status">
            {decidida.aprovado
              ? `Desconto do orçamento nº ${String(decidida.numero)} aprovado.`
              : `Desconto do orçamento nº ${String(decidida.numero)} recusado. Gere outro orçamento, se for o caso.`}
          </p>
        )}
      </div>
    </section>
  )
}
